import { useState } from 'react'
import {
  BrowserRouter,
  Routes,
  Route,
} from "react-router-dom";
import { SpeedInsights } from '@vercel/speed-insights/react';
import Index from './pages/Index'
import { createLazyRoute } from './components/LazyRoute';
import { ScrollToTop } from './components/ScrollToTop';
import { RedirectHandler } from './components/RedirectHandler';
import { Toaster } from "@/components/ui/toaster"
import { TooltipProvider } from "@/components/ui/tooltip"
import { CookieConsent } from "@/components/CookieConsent";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";
import GtmInitializer from "@/components/GtmInitializer";
import { MicrosoftClarity } from "@/components/MicrosoftClarity";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SEOHead } from "@/components/SEOHead";
import { HelmetProvider } from 'react-helmet-async';

// Lazy load components for code splitting
const Auth = createLazyRoute(() => import('./pages/Auth'));
const SetPassword = createLazyRoute(() => import('./pages/SetPassword'));
const Admin = createLazyRoute(() => import('./pages/Admin'));
const Blog = createLazyRoute(() => import('./pages/Blog'));
const BlogPost = createLazyRoute(() => import('./pages/BlogPost'));
const DynamicPage = createLazyRoute(() => import('./pages/DynamicPage'));
const NotFound = createLazyRoute(() => import('./pages/NotFound'));
const RobotsTxt = createLazyRoute(() => import('./pages/RobotsTxt'));
const LettingsPilot = createLazyRoute(() => import('./pages/LettingsPilot'));
const ThankYou = createLazyRoute(() => import('./pages/ThankYou'));
const Contact = createLazyRoute(() => import('./pages/Contact'));
const BusinessReviewConsultation = createLazyRoute(() => import('./pages/BusinessReviewConsultation'));
const ConsultationSuccess = createLazyRoute(() => import('./pages/ConsultationSuccess'));

// Service pages
const InboundVoiceAgents = createLazyRoute(() => import('./pages/services/inbound-voice-agents/InboundVoiceAgents'));
const RealtorOffice = createLazyRoute(() => import('./pages/services/inbound-voice-agents/RealtorOffice'));
const InsuranceCompanies = createLazyRoute(() => import('./pages/services/inbound-voice-agents/InsuranceCompanies'));
const HealthcareProviders = createLazyRoute(() => import('./pages/services/inbound-voice-agents/HealthcareProviders'));
const RestaurantChains = createLazyRoute(() => import('./pages/services/inbound-voice-agents/RestaurantChains'));
const CustomerService = createLazyRoute(() => import('./pages/services/inbound-voice-agents/CustomerService'));
const SaasCompanies = createLazyRoute(() => import('./pages/services/inbound-voice-agents/SaasCompanies'));

// Outbound Voice Agent pages
const SalesOutreach = createLazyRoute(() => import('./pages/services/outbound-voice-agents/SalesOutreach'));
const MarketResearch = createLazyRoute(() => import('./pages/services/outbound-voice-agents/MarketResearch'));
const AppointmentReminders = createLazyRoute(() => import('./pages/services/outbound-voice-agents/AppointmentReminders'));
const DebtCollection = createLazyRoute(() => import('./pages/services/outbound-voice-agents/DebtCollection'));
const MarketingAgency = createLazyRoute(() => import('./pages/services/outbound-voice-agents/MarketingAgency'));
const CustomerRetention = createLazyRoute(() => import('./pages/services/outbound-voice-agents/CustomerRetention'));

// Automation pages
const DocumentWorkflow = createLazyRoute(() => import('./pages/services/automation/DocumentWorkflow'));
const EmployeeOnboarding = createLazyRoute(() => import('./pages/services/automation/EmployeeOnboarding'));
const InventoryManagement = createLazyRoute(() => import('./pages/services/automation/InventoryManagement'));
const CustomerCommunications = createLazyRoute(() => import('./pages/services/automation/CustomerCommunications'));
const ProcessOrchestration = createLazyRoute(() => import('./pages/services/automation/ProcessOrchestration'));
const DataIntegration = createLazyRoute(() => import('./pages/services/automation/DataIntegration'));

// AI Copilot pages
const AgentAssistance = createLazyRoute(() => import('./pages/services/ai-copilots/AgentAssistance'));
const DocumentProcessing = createLazyRoute(() => import('./pages/services/ai-copilots/DocumentProcessing'));
const MeetingTranscription = createLazyRoute(() => import('./pages/services/ai-copilots/MeetingTranscription'));
const EmailDrafting = createLazyRoute(() => import('./pages/services/ai-copilots/EmailDrafting'));
const SchedulingOptimization = createLazyRoute(() => import('./pages/services/ai-copilots/SchedulingOptimization'));
const ResearchAssistance = createLazyRoute(() => import('./pages/services/ai-copilots/ResearchAssistance'));

function App() {
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
    document.body.classList.toggle('dark', !isDarkTheme);
  };

  // Fetch site settings for Google Analytics
  const { data: siteSettings } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_key, setting_value');
      
      if (error) throw error;
      
      // Convert array to object for easier access
      const settingsMap: Record<string, string> = {};
      data?.forEach((setting) => {
        settingsMap[setting.setting_key] = setting.setting_value;
      });
      
      return settingsMap;
    }
  });

  // Add console logging to debug routing
  console.log('App component mounted, current location:', window.location.pathname);

  return (
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <BrowserRouter>
          <ScrollToTop />
          <RedirectHandler />
          
          {/* SEO Head Management */}
          <SEOHead />
          
          {/* GTM Initializer - Load immediately */}
          <GtmInitializer />
          
          {/* Google Analytics - Load with defer for better performance */}
          <GoogleAnalytics measurementId={siteSettings?.ga4_measurement_id} />
          
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/admin/*" element={<Admin />} />
            <Route path="/robots.txt" element={<RobotsTxt />} />
            <Route path="/lettings-pilot" element={<LettingsPilot />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/business-review-consultation" element={<BusinessReviewConsultation />} />
            <Route path="/consultation-success" element={<ConsultationSuccess />} />
            
            {/* Service pages */}
            <Route path="/services/inbound-voice-agents" element={<InboundVoiceAgents />} />
            <Route path="/services/inbound-voice-agents/realtor-office" element={<RealtorOffice />} />
            <Route path="/services/inbound-voice-agents/insurance-companies" element={<InsuranceCompanies />} />
            <Route path="/services/inbound-voice-agents/healthcare-providers" element={<HealthcareProviders />} />
            <Route path="/services/inbound-voice-agents/restaurant-chains" element={<RestaurantChains />} />
            <Route path="/services/inbound-voice-agents/customer-service" element={<CustomerService />} />
            <Route path="/services/inbound-voice-agents/saas-companies" element={<SaasCompanies />} />
            
            {/* Outbound Voice Agent pages */}
            <Route path="/services/outbound-voice-agents/sales-outreach" element={<SalesOutreach />} />
            <Route path="/services/outbound-voice-agents/market-research" element={<MarketResearch />} />
            <Route path="/services/outbound-voice-agents/appointment-reminders" element={<AppointmentReminders />} />
            <Route path="/services/outbound-voice-agents/debt-collection" element={<DebtCollection />} />
            <Route path="/services/outbound-voice-agents/marketing-agency" element={<MarketingAgency />} />
            <Route path="/services/outbound-voice-agents/customer-retention" element={<CustomerRetention />} />

            {/* Automation pages */}
            <Route path="/services/automation/document-workflow" element={<DocumentWorkflow />} />
            <Route path="/services/automation/employee-onboarding" element={<EmployeeOnboarding />} />
            <Route path="/services/automation/inventory-management" element={<InventoryManagement />} />
            <Route path="/services/automation/customer-communications" element={<CustomerCommunications />} />
            <Route path="/services/automation/process-orchestration" element={<ProcessOrchestration />} />
            <Route path="/services/automation/data-integration" element={<DataIntegration />} />
            
            {/* AI Copilot pages */}
            <Route path="/services/ai-copilots/agent-assistance" element={<AgentAssistance />} />
            <Route path="/services/ai-copilots/document-processing" element={<DocumentProcessing />} />
            <Route path="/services/ai-copilots/meeting-transcription" element={<MeetingTranscription />} />
            <Route path="/services/ai-copilots/email-drafting" element={<EmailDrafting />} />
            <Route path="/services/ai-copilots/scheduling-optimization" element={<SchedulingOptimization />} />
            <Route path="/services/ai-copilots/research-assistance" element={<ResearchAssistance />} />
            
            <Route path="/:slug" element={<DynamicPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          
          {/* Cookie Consent Banner */}
          <CookieConsent />
          
          {/* Microsoft Clarity Analytics */}
          <MicrosoftClarity />
          
          {/* Vercel Speed Insights */}
          <SpeedInsights />
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  );
}

export default App;